import {
	all,
	takeLatest,
	takeEvery,
	call,
	put,
	select,
} from 'redux-saga/effects';
import { delay } from 'redux-saga';
import { I18n } from 'react-redux-i18n';
import {
	requestHandler,
	fetcher,
	getApiUrl,
	getApiHeaders,
} from '../../../../service/fetcher.saga';
import {
	SHOW_LOADER,
	HIDE_LOADER,
} from '../../../../Components/Loader/service/types';
import { notify } from '../../../../Components/Notifier/services/saga';
import { confirm } from '../../../../utils';
import { PARTNERS } from '../../config';
import { getOnboardingStepSelector } from './selector';
import {
	PRE_GET_PARTNERS_API,
	RECEIVE_PARTNERS_API,
	PRE_POST_STEP_STATUS,
	POST_STEP_STATUS_FINISHED,
} from './types';

// Affiche les notifications renvoyées par l'API :
//   - type 'popup'                       -> modale
//   - type warning/info/success/error    -> toast du type indiqué
export function* displayNotifications(notifications) {
	for (let notif of notifications || []) {
		if (!notif || !notif.message) continue;

		if (notif.type === 'popup') {
			confirm(notif.message, {
				title: I18n.t('general.information'),
				confirmLabel: I18n.t('general.close'),
				hideCancel: true,
			});
		} else {
			yield call(notify, notif.message, notif.type);
		}
	}
}

// GET /partners/all → liste des partenaires.
// action.prefix (optionnel) est envoyé en query param : l'API ne renvoie alors
// que ce partenaire, et le reducer ne met à jour que celui-là.
export function* watchGetPartnersApi(action = {}) {
	const refresh_token = window.localStorage.refresh_token;
	const prefix = action.prefix || null;
	const { result } = yield call(
		requestHandler,
		'partners',
		'GET',
		prefix ? { prefix } : {},
		'all',
		{},
		true,
		'',
		{ 'refresh-token': refresh_token }
	);

	if (result) {
		yield put({ type: RECEIVE_PARTNERS_API, result, prefix });
	}
}

// POST partnersConfig/step-status → { success, result: { notifications, partner } }.
//
// On appelle `fetcher` en direct (et non `requestHandler`) pour lire la réponse
// brute : requestHandler réduit tout échec métier à `result: false` et jette le
// payload, ce qui masquerait les notifications d'erreur d'onboarding. Ici on
// affiche les notifications succès *comme* échec, et on met à jour le partenaire
// renvoyé (même format que /partners/all).
export function* watchPostStepStatus(action) {
	yield put({ type: SHOW_LOADER });
	let response;
	try {
		// Route dédiée au front de cette app (authenticate user seul) ; même
		// handler que partnersConfig/step-status côté API.
		const url = `${getApiUrl()}/onboarding/step-status-front`;
		const headers = {
			...getApiHeaders(),
			'x-guid': sessionStorage.getItem('guid'),
			'refresh-token': window.localStorage.refresh_token,
		};
		response = yield call(fetcher, url, 'POST', action.body, headers);
	} catch (err) {
		// fetcher lève si le serveur n'a pas répondu (erreur réseau).
		response = null;
	} finally {
		yield put({ type: HIDE_LOADER });
	}

	const data = response && response.result;
	// Notif éventuelle au niveau racine (échec dur réseau/500).
	const rootNotif = response && response.notification;
	if (!data) {
		yield call(
			notify,
			rootNotif && rootNotif.message
				? rootNotif.message
				: I18n.t('general.error'),
			(rootNotif && rootNotif.type) || 'error'
		);
		return;
	}

	// Erreur serveur "best-effort" : l'API peut renvoyer un result (partner) ET
	// une notification d'erreur au niveau racine (cf. route 500). Sans ça elle
	// serait avalée, data étant truthy.
	if (rootNotif && rootNotif.message) {
		yield call(notify, rootNotif.message, rootNotif.type || 'error');
	}

	const prefix = action.body.prefix;
	let onboardingJustFinished = false;

	if (data.partner) {
		const previousStep = yield select(getOnboardingStepSelector, prefix);
		onboardingJustFinished =
			previousStep !== 'success' &&
			!!data.partner.onboarding &&
			data.partner.onboarding.step === 'success';

		// Le partner renvoyé ne porte pas le prefix : on le récupère du body.
		yield put({
			type: POST_STEP_STATUS_FINISHED,
			prefix,
			partner: data.partner,
		});
	}

	// TODO(onboarding): gestion fine des erreurs de validation multi-champs.
	// En cas d'échec, l'API renvoie data.error.ladditionResponseMessage.data =
	// [{ message, path }] (path[0] = clé de l'input fautif). À exploiter pour
	// surligner le(s) champ(s) concerné(s) dans la card (surtout resalys/thais
	// qui ont plusieurs inputs). Non branché pour l'instant : on se contente des
	// notifications (toasts/modales) affichées ci-dessous.
	yield call(displayNotifications, data.notifications);

	// Redirection post-onboarding : l'API peut renvoyer result.redirect (transient,
	// jamais dans l'état persistant → on ne redirige QUE sur le résultat du POST,
	// pas au GET). Sert aux OB "one-shot" externes (ex. ouilink, laddition) qui
	// passent configuré/actif chez nous puis renvoient vers le site du partenaire.
	// Comme le legacy : petit toast "info" puis redirection après quelques sec.
	if (data.redirect) {
		yield call(
			notify,
			I18n.t('partner.external_config_redirection'),
			'info',
			5000
		);
		yield delay(2500);
		window.location.href = data.redirect;
		return;
	}

	// Fin d'onboarding : si le partenaire a une page de réglages interne, on y
	// envoie l'utilisateur. Navigation complète (et non history.push) : la page
	// de réglages lit partnersConfig, que le nouvel onboarding ne met pas à jour
	// — le rechargement refait le fetchAll et repart d'un état frais.
	if (
		onboardingJustFinished &&
		PARTNERS[prefix] &&
		PARTNERS[prefix].component
	) {
		window.location.href = `/partners/${prefix}`;
	}
}

// Échange le code OAuth Ubereats contre la liste des stores. L'API persiste le
// refresh-token Ubereats côté serveur (variable uberEatsRefreshToken) ; on ne
// stocke donc rien côté front : au retour, un simple re-GET /partners/all fait
// resservir le step set_customer_id avec les stores en <select> (option b).
// NB API: nécessite une route "front" (authenticate user seul), équivalente à
// step-status-front — la route get-ubereats-stores exige authenticateService.
export function* fetchUbereatsStores(code) {
	const { result } = yield call(
		requestHandler,
		'onboarding',
		'POST',
		{ code },
		'get-ubereats-stores-front'
	);

	if (!result) {
		yield call(
			notify,
			I18n.t('partner.ubereats_wrong_initialization'),
			'error'
		);
		return false;
	}
	return true;
}

export default function* partnersApiSaga() {
	yield all([
		takeLatest(PRE_GET_PARTNERS_API, watchGetPartnersApi),
		takeEvery(PRE_POST_STEP_STATUS, watchPostStepStatus),
	]);
}
